import verificationVideoThumbnail from "@/assets/card-verification-video-thumbnail.jpg";
import { IconClose } from "@/assets/icons/geist/IconClose";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { StripePaymentForm } from "@/components/billing/StripePaymentForm";
import { Button } from "@/components/flexkit/Button";
import { VideoSection } from "@/components/VideoSection";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSegmentTrack, useSegmentTrackVideoClick } from "@/hooks/use-segment";
import { useStripePaymentRedirectResult } from "@/hooks/use-stripe-payment-redirect-result";
import { useCompleteBillingSetup, useSetupBilling } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import * as Dialog from "@radix-ui/react-dialog";
import type { PaymentIntent, StripeError } from "@stripe/stripe-js";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

const VERIFICATION_STEP = {
    askForVerification: "askForVerification",
    verificationForm: "verificationForm",
    success: "success",
    completingVerification: "completingVerification",
} as const;
type VerificationStep = (typeof VERIFICATION_STEP)[keyof typeof VERIFICATION_STEP];

const VERIFICATION_COPY = {
    title: "Help us keep bots out — get $10 in credits",
    description:
        "$1 authorization (refunded instantly) just to confirm you're human. Never a recurring charge.",
    ctaText: "I'm human — verify me",
    ctaDetails: "One-time verification. Immediate refund. Zero subscriptions.",
};

const verificationStepAtom = atom<VerificationStep>(VERIFICATION_STEP.askForVerification);
const paymentIntentIdAtom = atom("");
const clientSecretAtom = atom("");

export function CreditCardVerificationDialog({
    open,
    onOpenChange,
    onVerificationSuccess,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onVerificationSuccess: () => void;
}) {
    const setVerificationStep = useSetAtom(verificationStepAtom);
    const setPaymentIntentId = useSetAtom(paymentIntentIdAtom);
    const setClientSecret = useSetAtom(clientSecretAtom);
    const { redirectResult } = useStripePaymentRedirectResult();
    const { toast } = useToast();

    // Reset atoms when dialog closes to ensure fresh state on reopen
    useEffect(() => {
        if (!open) {
            setVerificationStep(VERIFICATION_STEP.askForVerification);
            setPaymentIntentId("");
            setClientSecret("");
        }
    }, [open, setVerificationStep, setPaymentIntentId, setClientSecret]);

    useEffect(() => {
        if (!redirectResult) {
            return;
        }
        if (redirectResult?.redirectStatus === "succeeded") {
            setPaymentIntentId(redirectResult.paymentIntent);
            setVerificationStep(VERIFICATION_STEP.completingVerification);
        } else {
            toast({
                title: "Verification failed",
                description: "Verification was cancelled or failed. Please try again.",
            });
            setVerificationStep(VERIFICATION_STEP.askForVerification);
        }
    }, [redirectResult, setVerificationStep, setPaymentIntentId, toast]);

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[200] bg-black bg-opacity-30 backdrop-blur-lg backdrop-opacity-100" />
                <Dialog.Content
                    className="fixed bottom-auto left-1/2 right-auto top-1/2 z-[300] flex max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl bg-white/40 p-2 dark:bg-neutral-700 md:max-w-lg"
                    onPointerDownOutside={(event) => {
                        // Prevent dismissing the credit card verification dialog by clicking outside
                        event.preventDefault();
                    }}
                >
                    <CloseDialogButton />
                    <div className="flex max-h-[calc(100vh-3rem)] w-full flex-col items-center justify-center overflow-hidden rounded-[12px] bg-white pt-12 shadow dark:bg-surface-primary">
                        <CurrentStep onVerificationSuccess={onVerificationSuccess} />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function CloseDialogButton() {
    const currentStep = useAtomValue(verificationStepAtom);
    const isDialogClosable = currentStep !== VERIFICATION_STEP.completingVerification;

    return (
        <Dialog.Close
            disabled={!isDialogClosable}
            className={cn(
                "ring-offset-background absolute right-6 top-6 rounded-full p-1 opacity-70 transition-opacity hover:bg-surface-03 hover:opacity-100 focus:ring-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary active:outline-0 disabled:bg-transparent disabled:text-content-secondary",
                isDialogClosable ? "block" : "hidden",
            )}
        >
            <IconClose size="base" className="text-content-primary" />
            <span className="sr-only">Close</span>
        </Dialog.Close>
    );
}

function CurrentStep({ onVerificationSuccess }: { onVerificationSuccess: () => void }) {
    const verificationStep = useAtomValue(verificationStepAtom);
    switch (verificationStep) {
        case VERIFICATION_STEP.askForVerification:
            return <AskForVerificationStep />;
        case VERIFICATION_STEP.verificationForm:
            return <VerificationFormStep />;
        case VERIFICATION_STEP.completingVerification:
            return <CompletingVerificationStep onVerificationSuccess={onVerificationSuccess} />;
        case VERIFICATION_STEP.success:
            return null;
    }
}

function AskForVerificationStep() {
    const trackVideoClick = useSegmentTrackVideoClick();

    const handleVideoPlay = useCallback(() => {
        trackVideoClick("verification-explanation");
    }, [trackVideoClick]);

    return (
        <>
            <Dialog.Title className="mb-2 text-center text-2xl font-bold">{VERIFICATION_COPY.title}</Dialog.Title>
            <Dialog.Description className="mx-6 text-center text-content-muted md:mx-10">
                {VERIFICATION_COPY.description}
            </Dialog.Description>
            <div className="aspect-video w-full p-6">
                <VideoSection
                    autoPlay
                    muted
                    className="drop-shadow-none"
                    playbackId="JB6FyiweVdFw2nQzFGFSJOYlyT01hSvhM5oZZu4eJ7q8"
                    metadataVideoTitle="The Future of Software Development with ONA"
                    defaultHiddenCaptions={false}
                    poster={verificationVideoThumbnail}
                    onPlay={handleVideoPlay}
                    accentColor="rgb(var(--ona-blue-200))"
                    style={{
                        "--seek-backward-button": "none",
                        "--seek-forward-button": "none",
                    }}
                />
            </div>
            <div className="mb-2 w-full px-6">
                <BeginVerificationButton />
            </div>
            <p className="mb-8 text-center text-base text-content-primary">{VERIFICATION_COPY.ctaDetails}</p>
        </>
    );
}

function BeginVerificationButton() {
    const { mutateAsync: setupBilling, isPending } = useSetupBilling();
    const setClientSecret = useSetAtom(clientSecretAtom);
    const setVerificationStep = useSetAtom(verificationStepAtom);
    const { toast } = useToast();
    const track = useSegmentTrack();

    async function beginVerification() {
        track("verification_started");
        try {
            const response = await setupBilling();
            setClientSecret(response.setupIntentClientSecret);
            setVerificationStep(VERIFICATION_STEP.verificationForm);
        } catch (error) {
            track("verification_failed", {
                step: "setup_billing",
                error: formatError(error),
            });
            toast({
                title: "Failed to begin verification",
                description: formatError(error),
            });
        }
    }

    return (
        <Button
            variant="primary"
            className="w-full text-base"
            size="lg"
            onClick={beginVerification}
            loading={isPending}
            disabled={isPending}
            data-tracking-id="get-10-in-credits-for-free"
        >
            {VERIFICATION_COPY.ctaText}
        </Button>
    );
}

function VerificationFormStep() {
    const [isFormScrolled, setIsFormScrolled] = useState(false);
    const clientSecret = useAtomValue(clientSecretAtom);
    const setPaymentIntentId = useSetAtom(paymentIntentIdAtom);
    const setVerificationStep = useSetAtom(verificationStepAtom);
    const { toast } = useToast();
    const formContainerRef = useRef<HTMLDivElement>(null);
    const track = useSegmentTrack();

    useEffect(() => {
        function handleScroll(this: HTMLDivElement) {
            setIsFormScrolled(this.scrollTop > 0);
        }
        if (formContainerRef.current) {
            const formContainer = formContainerRef.current;
            formContainer.addEventListener("scroll", handleScroll);
            return () => {
                formContainer.removeEventListener("scroll", handleScroll);
            };
        }
    }, []);

    const handleSuccess = useCallback(
        (paymentIntent: PaymentIntent) => {
            setPaymentIntentId(paymentIntent.id);
            setVerificationStep(VERIFICATION_STEP.completingVerification);
        },
        [setPaymentIntentId, setVerificationStep],
    );

    const handleError = useCallback(
        (error: StripeError | Error) => {
            track("verification_failed", {
                step: "stripe_payment",
                error: formatError(error),
            });
            toast({
                title: "Failed to complete verification",
                description: formatError(error),
            });
        },
        [toast, track],
    );

    return (
        <>
            <Dialog.Title className="text-center text-2xl font-bold">Add credit card details</Dialog.Title>
            <Dialog.Description
                className={cn(
                    "w-full border-b border-b-transparent px-12 pb-4 text-center opacity-50 transition-colors",
                    {
                        "border-b-border-base": isFormScrolled,
                    },
                )}
            >
                To prevent abuse and crypto mining, we run a $1 authorization and refund it right away.
            </Dialog.Description>
            {!clientSecret && <IconSpinner className="mb-4 animate-spin" size="lg" />}
            <div
                ref={formContainerRef}
                className={cn("w-full overflow-auto px-8 pb-8 pt-4", {
                    hidden: !clientSecret,
                })}
            >
                <StripePaymentForm
                    clientSecret={clientSecret}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    submitButtonText="Continue"
                    submitButtonClassName="text-lg"
                    data-tracking-id="continue-verification-form-step"
                />
            </div>
        </>
    );
}

function CompletingVerificationStep({ onVerificationSuccess }: { onVerificationSuccess: () => void }) {
    const paymentIntentId = useAtomValue(paymentIntentIdAtom);
    const completeBillingSetup = useCompleteBillingSetup();
    const track = useSegmentTrack();
    const { toast } = useToast();

    useEffect(() => {
        if (paymentIntentId && !completeBillingSetup.isPending && !completeBillingSetup.isSuccess) {
            completeBillingSetup.mutate(paymentIntentId, {
                onSuccess: () => {
                    track("verification_completed");
                    onVerificationSuccess();
                },
                onError: (error) => {
                    track("verification_failed", {
                        step: "complete_billing_setup",
                        error: formatError(error),
                    });
                    toast({
                        title: "Failed to complete verification",
                        description: formatError(error),
                    });
                },
            });
        }
    }, [paymentIntentId, completeBillingSetup, onVerificationSuccess, track, toast]);

    return (
        <>
            <div className="mb-8 rounded bg-[#1E3C2C] p-4">
                <OnaLettermark className="animate-pulse text-white opacity-90" />
            </div>
            <Dialog.Title className="mb-2 w-full text-center text-2xl font-bold">Completing verification</Dialog.Title>
            <Dialog.Description className="mx-12 mb-8 text-center opacity-50">
                This won&apos;t be long.
            </Dialog.Description>
        </>
    );
}
